import type React from "react";
import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { SettingsStyled } from "./settings.styles";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { settingsSchema } from "../../../../utils/validations";
import { getRuleset, updateRuleset } from "../../../../actions";
import { Button } from "../../../../components/button/button";
import TextInputFormField from "../../../../components/form-fields/text-input-field/text-input-form-field";
import SelectFormField from "../../../../components/form-fields/select-field/select-form-field";
import Loader from "../../../../components/loader/loader";
import { addNotification } from "../../../../store/slices/ui-slice";
import { v4 as uuid } from "uuid";
import { useAppDispatch } from "../../../../store/hooks";
import { SwitchFormField } from "../../../../components/form-fields/switch-field/switch-form-field";

interface RulesetData {
  workday_start_utc: string;
  workday_end_utc: string;
  time_slot_limit: number;
  block_gen_limit: number;
  block_gen_min: number;
  block_gen_offset: number;
  enable_matrix_filtering: boolean;
  enable_additional_rules: boolean;
  enable_reserve_filtering: boolean;
  address?: string;
  lat?: string;
  lon?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
}

interface FormData {
  workday_start_utc: string;
  workday_end_utc: string;
  time_slot_limit: number;
  block_gen_limit: number;
  block_gen_min: number;
  block_gen_offset: number;
  enable_matrix_filtering: boolean;
  enable_additional_rules: boolean;
  enable_reserve_filtering: boolean;
  address?: string;
  lat?: string;
  lon?: string;
}

const Settings: React.FC = () => {
  const [rulesetData, setRulesetData] = useState<RulesetData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm<any>({
    resolver: yupResolver(settingsSchema),
    mode: "all",
  });

  useEffect(() => {
    const fetchRuleset = async () => {
      try {
        const response = await getRuleset();
        if (response.data?.data) {
          setRulesetData(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch ruleset:", error);
        dispatch(
          addNotification({
            id: uuid(),
            type: "error",
            message: "Failed to fetch ruleset",
          })
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRuleset();
  }, [dispatch]);

  const onSubmit = async (data: FormData) => {
    if (!rulesetData) return;
    const payload: RulesetData = {
      ...data,
    };

    console.log("Here is the payload", payload);

    setSubmitting(true);
    try {
      await updateRuleset(payload);
      dispatch(
        addNotification({
          id: uuid(),
          type: "success",
          message: "Ruleset updated successfully!",
        })
      );
    } catch (error: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: error.response?.data?.message || "Failed to update ruleset",
        })
      );
    } finally {
      setSubmitting(false);
    }
  };
  if (loading) return <Loader absolute />;

  return (
    <SettingsStyled>
      <Box className="header">
        <Typography variant="h2">Settings</Typography>
        <div className="settings-card-wrapper">
          <Box className="card-wrapper">
            <div className="content-wrapper">
              <form
                className="fields-wrapper"
                onSubmit={handleSubmit(onSubmit)}
              >
                <TextInputFormField
                  name="workday_start_utc"
                  control={control}
                  label="Workday Start (UTC)"
                  defaultValue={rulesetData?.workday_start_utc || "14:30:00"}
                  placeholder="Enter start time"
                />
                <TextInputFormField
                  name="workday_end_utc"
                  control={control}
                  label="Workday End (UTC)"
                  defaultValue={rulesetData?.workday_end_utc || "22:30:00"}
                  placeholder="Enter end time"
                />
                <TextInputFormField
                  name="time_slot_limit"
                  control={control}
                  label="Time Slot Limit"
                  defaultValue={
                    rulesetData?.time_slot_limit !== undefined
                      ? String(rulesetData.time_slot_limit)
                      : "0"
                  }
                  placeholder="Enter time slot limit"
                />
                <TextInputFormField
                  name="block_gen_limit"
                  control={control}
                  label="Block Generation Limit"
                  defaultValue={
                    rulesetData?.block_gen_limit !== undefined
                      ? String(rulesetData.block_gen_limit)
                      : "0"
                  }
                  placeholder="Enter block generation limit"
                />
                <TextInputFormField
                  name="block_gen_min"
                  control={control}
                  label="Block Generation Minimum"
                  defaultValue={
                    rulesetData?.block_gen_min !== undefined
                      ? String(rulesetData.block_gen_min)
                      : "0"
                  }
                  placeholder="Enter block generation minimum"
                />
                <TextInputFormField
                  name="block_gen_offset"
                  control={control}
                  label="Block Generation Offset"
                  defaultValue={
                    rulesetData?.block_gen_offset !== undefined
                      ? String(rulesetData.block_gen_offset)
                      : "0"
                  }
                  placeholder="Enter block generation offset"
                />
                <SelectFormField
                  name="enable_matrix_filtering"
                  control={control}
                  label="Enable Matrix Filtering"
                  defaultValue={
                    rulesetData?.enable_matrix_filtering ? "true" : "false"
                  }
                  options={[
                    { label: "Enabled", value: "true" },
                    { label: "Disabled", value: "false" },
                  ]}
                />
                <SelectFormField
                  name="enable_additional_rules"
                  control={control}
                  label="Enable Additional Rules"
                  defaultValue={
                    rulesetData?.enable_additional_rules ? "true" : "false"
                  }
                  options={[
                    { label: "Enabled", value: "true" },
                    { label: "Disabled", value: "false" },
                  ]}
                />
                <SelectFormField
                  name="enable_reserve_filtering"
                  control={control}
                  label="Enable Reserve Filtering"
                  defaultValue={
                    rulesetData?.enable_reserve_filtering ? "true" : "false"
                  }
                  options={[
                    { label: "Enabled", value: "true" },
                    { label: "Disabled", value: "false" },
                  ]}
                />
                <TextInputFormField
                  name="lookahead"
                  control={control}
                  label="Lookahead (days)"
                  defaultValue={
                    rulesetData?.lookahead !== undefined
                      ? String(rulesetData.lookahead)
                      : "1"
                  }
                  placeholder="Enter number of days to look ahead"
                />
                <SwitchFormField
                  name="enabled"
                  control={control}
                  label="Enable Ruleset"
                  defaultValue={rulesetData?.enabled ?? false}
                />
              </form>
              <div className="button-wrapper">
                <Button
                  disabled={!isValid || !isDirty || submitting}
                  isLoading={submitting}
                  variant="contained"
                  color="primary"
                  fullWidth
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </Box>
        </div>
      </Box>
    </SettingsStyled>
  );
};

export default Settings;
